<script setup lang="ts">
import { cloneDeep, isEqual } from 'lodash-es';
import Dropdown, { DropdownChangeEvent } from 'primevue/dropdown';
import InputGroupAddon from 'primevue/inputgroupaddon';
import { useField } from 'vee-validate';
import {
  computed,
  inject,
  nextTick,
  onMounted,
  reactive,
  ref,
  shallowRef,
  watch,
} from 'vue';

import Badge from 'lib/components/badge/Badge.vue';
import { BadgeProps } from 'lib/components/badge/Badge.vue.d';
import type {
  DropdownEmits,
  DropdownProps,
  Option,
  OptionValue,
} from 'lib/components/dropdown/Dropdown.vue.d';
import FieldWrapper from 'lib/components/fieldwrapper/FieldWrapper.vue';
import Icon from 'lib/components/icon/Icon.vue';
import InputGroup from 'lib/components/inputgroup/InputGroup.vue';
import ValidatorMessage from 'lib/components/validatormessage/ValidatorMessage.vue';
import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import { default as filterOptions } from 'lib/utils/filterOptions.util';
import { default as mergePropsWithDefaults } from 'lib/utils/mergePropsWithDefaults.util';
import { formatVowelSoundLabel } from 'lib/utils/textFormatter.util';

import { FieldValidation } from '../form/Form.vue.d';
import { Nullable } from '../ts-helpers.d';

const props = withDefaults(defineProps<DropdownProps>(), {
  filter: true,
  modelValue: undefined,
  formatValidatorMessage: true,
  valueType: 'plain',
  showOptionalText: undefined,
  allowNullOption: undefined,
  initialValue: undefined,
  panelMaxContent: false,
});

const emit = defineEmits<DropdownEmits>();

onMounted(() => {
  setValidator();
});

const { locale, defaultProps } = useWangsVueConfig('Dropdown');
const config = computed(() => mergePropsWithDefaults(props, defaultProps));

const Preset = inject<Record<string, any>>('preset', {}).dropdown;
const InputGroupAddonPreset = inject<Record<string, any>>(
  'preset',
  {},
).inputgroupaddon;

const dropdown = ref<Dropdown>();
const isShowOverlay = shallowRef<boolean>(false);
const isClicked = shallowRef<boolean>(false);

const invalidInput = computed(() => props.invalid || !!field.errorMessage);

const field = reactive<FieldValidation<Nullable<OptionValue>>>({
  value:
    props.initialValue !== undefined ? props.initialValue : props.modelValue,
});

const emptyErrorMessage = computed(() => {
  return locale.emptySelectionErrorMessage
    .replace('{label}', props.label)
    .replace(
      '{formattedLabel}',
      formatVowelSoundLabel(props.label)?.toLowerCase(),
    );
});

const visibleOptions = computed(() => filterOptions(props.options as string[]));

const dropdownPlaceholder = computed(() => {
  return props.loading
    ? locale.loadingPlaceholder
    : (props.placeholder ??
        locale.inputPlaceholder
          ?.replace('{label}', props.label ?? '')
          .replace('{lowercaseLabel}', (props.label ?? '').toLowerCase()));
});

const setValidator = (): void => {
  if (props.useValidator) {
    Object.assign(
      field,
      useField(props.fieldName ?? 'dropdown', (value: string | number) => {
        /*
         * '!=' checks wether the value is undefined or null
         * Because `undefined == null` but `undefined !== null`
         */

        const hasValue = value != null && value !== '';

        if (!hasValue && props.mandatory) {
          if (
            typeof props.validatorMessage === 'object' &&
            'empty' in props.validatorMessage
          ) {
            return props.validatorMessage.empty;
          }

          return (
            (props.validatorMessage as string | boolean) ??
            emptyErrorMessage.value
          );
        }

        return nextTick(setValidatorMessage);
      }),
    );

    if (props.initialValue !== undefined) field.value = props.initialValue;
    if (props.invalid) field.validate();
  }
};

const setValidatorMessage = (): boolean | string => {
  if (props.validatorMessage && props.invalid) {
    return props.validatorMessage as string;
  }

  return true;
};

const updateFieldValue = (event: DropdownChangeEvent): void => {
  const { value } = event;
  if (props.unselectOnReselect) isClicked.value = true;
  else emit('update:modelValue', value);
};

const getOptionLabel = (): string => {
  if (props.optionValue) {
    const matchOption = visibleOptions.value.find((op) => {
      if (typeof op != 'string') {
        if (props.dataKey) {
          return (
            field.value[props.dataKey] ===
            op[(props.optionValue ?? '') as keyof Option][props.dataKey]
          );
        }

        return isEqual(
          cloneDeep(op[(props.optionValue ?? '') as keyof Option]),
          cloneDeep(field.value),
        );
      }
    });

    if (matchOption && typeof matchOption != 'string') {
      return matchOption[(props.optionLabel ?? '') as keyof Option] as string;
    }
  } else if (
    field.value &&
    typeof field.value === 'object' &&
    props.optionLabel &&
    props.optionLabel in field.value
  ) {
    return field.value[props.optionLabel] as string;
  }

  return field.value as string;
};

/**
 * Method to show the overlay with template ref.
 */
const showOverlay = (): void => {
  if (dropdown.value) dropdown.value.show();
};

watch(
  () => props.initialValue,
  (value) => {
    field.value = value;
  },
  { once: true },
);

watch(
  () => props.modelValue,
  (value) => {
    field.value = value;
  },
);

watch(
  () => field.value,
  (newValue, oldValue) => {
    setTimeout(() => {
      if (props.unselectOnReselect && isClicked.value) {
        if (isEqual(newValue, oldValue) && newValue !== undefined) {
          field.value = undefined;
        }
        isClicked.value = false;
        emit('update:modelValue', field.value);
      }
    }, 0);
  },
);

defineExpose({
  showOverlay,
});
</script>

<template>
  <FieldWrapper
    :info="fieldInfo"
    :label="props.label"
    :mandatory="props.mandatory"
    :show-optional-text="showOptionalText"
    @info-click="$emit('fieldInfoClick')"
  >
    <InputGroup
      :class="[
        {
          'grid grid-cols-[max-content,1fr,max-content]':
            $slots['addon-left'] && $slots['addon-right'],
          'grid grid-cols-[1fr,max-content]':
            !$slots['addon-left'] && $slots['addon-right'],
          'grid grid-cols-[max-content,1fr]':
            $slots['addon-left'] && !$slots['addon-right'],
        },
      ]"
      :disabled="props.disabled"
      :invalid="invalidInput"
      :ring="inputBorder"
    >
      <InputGroupAddon v-if="$slots['addon-left']" :disabled="props.disabled">
        <slot name="addon-left" />
      </InputGroupAddon>

      <Dropdown
        ref="dropdown"
        v-bind="props"
        v-model="field.value"
        :aria-describedby="props.label + 'error'"
        :filter-placeholder="
          props.filterPlaceholder || locale.filterPlaceholder
        "
        :invalid="invalidInput"
        :options="visibleOptions"
        :placeholder="dropdownPlaceholder"
        :pt="{
          list: Preset?.list({ props }),
          panel: Preset?.panel({ props }),
          virtualScroller: Preset?.virtualScroller({ props }),
          wrapper: Preset?.wrapper({ props }),
        }"
        :virtual-scroller-options="
          visibleOptions?.length > 10 ? { itemSize: 32 } : undefined
        "
        @change="updateFieldValue"
        @hide="isShowOverlay = false"
        @show="$emit('show'), (isShowOverlay = true)"
      >
        <template #value="{ placeholder }" v-if="loading">
          <span v-bind="Preset.loadingplaceholder">{{ placeholder }}</span>
        </template>

        <template #value="slotProps" v-else>
          <template
            v-if="
              config.allowNullOption
                ? slotProps.value !== undefined
                : slotProps.value != null
            "
          >
            <slot
              :original-value="slotProps.value"
              :value="getOptionLabel()"
              name="value"
            >
              <Badge
                v-if="valueType === 'badge'"
                v-bind="badgeValueProps as BadgeProps"
                :label="getOptionLabel()"
              />

              <template v-else>
                {{ getOptionLabel() }}
              </template>
            </slot>
          </template>

          <template v-else>
            {{ slotProps.placeholder }}
          </template>
        </template>

        <template #dropdownicon>
          <Icon v-bind="Preset.dropdownicon({ state: { isShowOverlay } })" />
        </template>

        <template #loadingicon>
          <Icon v-bind="Preset.loadingicon" />
        </template>

        <template #option="{ option }" v-if="$slots['option']">
          <slot :option="option" name="option" />
        </template>

        <template #filtericon>
          <Icon v-bind="Preset.filtericon" />
        </template>

        <template #footer="{ value, options }" v-if="$slots['footer']">
          <slot :options="options" :value="value" name="footer" />
        </template>
      </Dropdown>

      <InputGroupAddon
        v-if="$slots['addon-right']"
        :pt="{
          root: InputGroupAddonPreset.root({
            props: {
              class: [
                {
                  '!text-general-200': props.disabled,
                },
              ],
            },
          }),
        }"
      >
        <slot name="addon-right" />
      </InputGroupAddon>
    </InputGroup>

    <ValidatorMessage
      :id="props.label + 'error'"
      :message="field.errorMessage"
    />
  </FieldWrapper>
</template>
